import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useCustomToast } from "../../../components/CustomToast/CustomToast";
import { processesService } from "../../../services/processesService";
import { getEmptyPageResponse, toTablePageableData } from "../../../utils";
import {
    Box,
    Button,
    Flex,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    Stack,
} from "@chakra-ui/react";
import { AddIcon } from "@chakra-ui/icons";
import Card from "../../../components/Card/Card";
import CardBody from "../../../components/Card/CardBody";
import SearchTable2 from "../../../components/Tables/SearchTable2";
import Loading from "../../../components/Loading/Loading";
import { ROUTES } from "../../../constants/routes-path";
import { IoIosArrowDown } from "react-icons/io";
import { ProcessFilter } from "./ProcessFilter";
import { FaFileExcel } from "react-icons/fa";
import ConfirmDialog from "components/Dialog/ConfirmDialog";
import { generatePrintCaHtml } from "./ProcessPrintCaTemplate";
import ProcessSpecificMenu from "./ProcessSpecificMenu";
import { ProcessGeneralActions } from "./ProcessGeneralActions";
import ListPageCardHeader from "components/Card/ListPageCardHeader";
import { PROCESS_LIST_URL_DEFAULTS } from "utils/listUrlSync";
import { useListUrlSync } from "hooks/useListUrlSync";
import { ListFiltersActiveAlert } from "components/ListFiltersActiveAlert/ListFiltersActiveAlert";

function ProcessesList() {
    const { t } = useTranslation();
    const { searchParams, setSearchParams, hasActiveFilters } = useListUrlSync(PROCESS_LIST_URL_DEFAULTS);
    const [response, setResponse] = useState(undefined);
    const navigate = useNavigate();
    const showToast = useCustomToast();
    const deleteDialogRef = useRef(undefined);
    const reassignDialogRef = useRef(undefined);
    const reopenDialogRef = useRef(undefined);
    const [currentSelectedId, setCurrentSelectedId] = useState(null);
    const [isLoadingResponse, setIsLoadingResponse] = useState(true);
    const [isLoadingExport, setIsLoadingExport] = useState(false);

    const handleExportData = async () => {
        try {
            setIsLoadingExport(true);
            const { data, headers } = await processesService.export();
            const fileName = headers['content-disposition'].match(/filename="(.+)"/)[1];

            const url = URL.createObjectURL(new Blob([data]));
            Object.assign(document.createElement('a'), {
                href: url,
                download: fileName
            }).click();

            URL.revokeObjectURL(url);

        } catch (error) {
            showToast({ title: t(error), status: "error" });
        }
        finally {
            setIsLoadingExport(false);
        }
    }

    async function handleReassignProcess() {
        try {
            await processesService.reassign(currentSelectedId);
            showToast({ title: t("process.messages.success.reassign"), status: "success" });
            navigate(ROUTES.processesConfirmReceiptPath.replace(":id", currentSelectedId))
        } catch (e) {
            showToast({ title: t(e), status: "error" });
        }
    }

    async function handleReopenProcess() {
        try {
            await processesService.reopen(currentSelectedId);
            fetchData(searchParams)
            showToast({ title: t("system.notifications.success"), status: "success" });
        } catch (e) {
            showToast({ title: t(e), status: "error" });
        }
    }

    async function handlePrintLetter(row) {
        window.open(
            `${window.location.origin}${ROUTES.processLetterPdf.replace(":id", row.id)}`,
            '_blank'
        );
    }

    const handleRemove = (id) => {
        setCurrentSelectedId(id);
        deleteDialogRef.current?.displayDialog({
            title: t("process.list.actions.remove-process"),
            message: t("system.actions.remove-confirm"),
        });
    };

    const columns = useMemo(() => [
        {
            Header: t("process.list.table.header.number"),
            accessor: "numero",
            Cell: ({ row }) => (
                <Flex align="center" gap={2}>
                    <ProcessSpecificMenu
                        row={row}
                        t={t}
                        navigate={navigate}
                        openProcessPrintCaPage={openProcessPrintCaPage}
                        handleAttachmentDocument={handleAttachmentDocument}
                        handleReassign={handleReassign}
                        handleReopen={handleReopen}
                        handlePrintLetter={handlePrintLetter}
                    />
                    {row.original.numero}
                </Flex>
            ),
        },
        {
            Header: t("process.list.table.header.ait"),
            accessor: "infracao.numero_ait"
        },
        {
            Header: t("process.list.table.header.vehicle_plate"),
            accessor: "infracao.placa_veiculo",
        },
        {
            Header: t("process.list.table.header.creation"),
            accessor: "data_criacao"
        },
        {
            Header: t("process.list.table.header.vehicle_owner"),
            accessor: "infracao.nome_proprietario_veiculo_ait",
        },
        {
            Header: t("process.list.table.header.driver"),
            accessor: "nome_condutor"
        },
        {
            Header: t("process.list.table.header.trial"),
            accessor: "data_limite_julgamento"
        },
        ...(response?.is_responsible_visible
            ? [{
                Header: t("process.list.table.header.responsible"),
                accessor: "usuario_responsavel_atual.pessoa.nome"
            }]
            : []),
        {
            Header: t("process.list.table.header.sector"),
            accessor: "unidade_administrativa_responsavel.sigla"
        },
        {
            Header: t("process.list.table.header.status"),
            accessor: "status",
            disableSortBy: true
        },
        {
            Header: t("process.list.table.header.actions"),
            id: "actions",
            disableSortBy: true,
            actions: ProcessGeneralActions(t, navigate, handleRemove),
        }
    ], [response]);

    const fetchData = async () => {
        setIsLoadingResponse(true);
        if (response) setResponse(undefined);

        try {
            const data = await processesService.list(searchParams);
            setResponse(data);
        } catch (err) {
            setResponse(getEmptyPageResponse(searchParams));
            showToast({ title: t(err), status: "error" });
        } finally {
            setIsLoadingResponse(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, [searchParams]);

    const newProcess = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes + ROUTES.processesAdd);
    }

    const printNPsForJudgement = () => {
        navigate(ROUTES.transportManagementPath + ROUTES.processes + ROUTES.processPrintNpsTrial);
    };

    const handleReassign = (id) => {
        setCurrentSelectedId(id);
        reassignDialogRef.current?.displayDialog({
            title: t("process.list.actions.reassign-process"),
            message: t("process.list.actions.reassign-process-confirmation-message")
        });
    };

    const handleReopen = (id) => {
        setCurrentSelectedId(id);
        reopenDialogRef.current?.displayDialog({
            title: t("process.list.table.actions.reopen-process"),
            message: t("process.list.actions.reopen-process-confirmation-message")
        });
    };

    const handleConfirmRemove = async () => {
        if (currentSelectedId) {
            try {
                await processesService.delete(currentSelectedId);
                showToast({ title: t('process.messages.success.removed'), status: "success" });
                fetchData();
            } catch (err) {
                showToast({ title: t(err), status: "error" });
            }
        }
    };

    const openProcessPrintCaPage = (process) => {
        const url = ROUTES.processPrintCaPath.replace(':id', process.id);
        window.open(url, '_blank');
    };

    const handleAttachmentDocument = (processId) => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.pdf, .jpeg, .jpg';

        input.onchange = async () => {
            const file = input.files?.[0];
            if (file) {
                const formData = new FormData();
                formData.append("anexo", file);

                try {
                    await processesService.attachmentDocument(processId, formData);
                    showToast({ title: t("process.messages.success.attach-document"), status: "success" });
                    fetchData();
                } catch (err) {
                    showToast({ title: t(err), status: "error" });
                }
            }
        };

        input.click();
    };

    const updateSearchParams = (params = {}) => {
        const page = Math.min(params?.page ?? 1, response?.total_pages ?? Infinity) || 1;
        if (params) setSearchParams({ ...searchParams, ...params, page: page });
    };

    const clearProcessFilters = () => {
        updateSearchParams({
            page: 1,
            filters: {
                placa: null,
                numero_ait: null,
                numero: null,
                data_inicial: null,
                data_final: null,
                id_unidade_administrativa_responsavel: null,
                id_usuario_responsavel_atual: null,
                julgamento: null,
            },
        });
    };

    return (
        <Flex direction="column" gap="5" pt={{ base: "150px", lg: "90px" }}>
            <Flex w="100%" px="30px" justify="space-between" align="center" gap="4" direction={{ sm: "column", md: "row" }}>
                {/*Main action*/}
                <Button
                    w="145px"
                    variant="list-primary"
                    leftIcon={<AddIcon fontSize="sm" />}
                    alignSelf={{ sm: "flex-start", lg: null }}
                    onClick={newProcess}
                >
                    {t("process.list.actions.new")}
                </Button>

                <Stack direction="row" spacing="10px" alignSelf={{ sm: "flex-start", lg: "auto" }}>
                    {/*Filters menu*/}
                    <ProcessFilter
                        filterSnapshot={searchParams.filters}
                        onFilter={(filters) => updateSearchParams({ filters })}
                    />

                    {/*Extra actions menu*/}
                    <Menu>
                        <MenuButton
                            w="125px"
                            variant="list-primary"
                            rightIcon={<IoIosArrowDown />}
                            as={Button}
                        >
                            {t("system.labels.actions")}
                        </MenuButton>

                        <MenuList variant="light">
                            <MenuItem onClick={newProcess} icon={<AddIcon />}>
                                {t("process.list.actions.new")}
                            </MenuItem>
                            <MenuItem icon={<AddIcon />} onClick={printNPsForJudgement}>
                                {t("process.list.actions.print-nps-trial")}
                            </MenuItem>
                            <MenuItem onClick={handleExportData} icon={<FaFileExcel />}>
                                {t("process.list.actions.export")}
                            </MenuItem>
                        </MenuList>
                    </Menu>
                </Stack>
            </Flex>

            <Card overflowX={{ sm: "scroll", lg: "hidden" }}>
                <ListPageCardHeader title={t("process.title")} subtitle={t("process.description")} />
                <CardBody>
                    <ListFiltersActiveAlert show={hasActiveFilters} onClear={clearProcessFilters} />
                    {isLoadingResponse ? (
                        <Loading maxH={{ sm: "100px", md: "350px" }} />
                    ) : (
                        <Box position="relative">
                            <SearchTable2
                                columnsData={columns}
                                disableSearch={true}
                                tableData={response?.process_dto_list?.content ?? []}
                                pageableData={toTablePageableData(response?.process_dto_list ?? {}, { defaultField: "id", ...searchParams })}
                                onChange={(params) => {
                                    updateSearchParams(params);
                                }}
                                onRowClick={(original) => {
                                    navigate(ROUTES.processesDetailsPath.replace(":id", original?.id));
                                }}
                            />

                            {isLoadingExport && (
                                <Box
                                    position="fixed"
                                    top="0"
                                    left="0"
                                    right="0"
                                    bottom="0"
                                    bg="rgba(255, 255, 255, 0.6)"
                                    display="flex"
                                    justifyContent="center"
                                    alignItems="center"
                                    zIndex="overlay"
                                >
                                    <Loading maxH={{ sm: "100px", md: "350px" }} />
                                </Box>
                            )}
                        </Box>
                    )}
                </CardBody>
            </Card>

            <ConfirmDialog ref={deleteDialogRef} onConfirm={handleConfirmRemove} confirmButtonProps={{ colorScheme: "red" }} />
            <ConfirmDialog ref={reassignDialogRef} onConfirm={handleReassignProcess} confirmButtonProps={{ variant: "list-primary" }} />
            <ConfirmDialog ref={reopenDialogRef} onConfirm={handleReopenProcess} confirmButtonProps={{ variant: "list-primary" }} />
        </Flex>
    )
}

export default ProcessesList;
